/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * LoginPage — Split-screen "Ultimate" login with boot animation sequence.
 *
 * Design: Left brand panel (canvas particles + hex grid) | vertical accent | Right form.
 * Animations: preboot typing → global slit reveal → right-panel slit → staggered fields.
 * Does NOT use AuthLayout — fully self-contained layout.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, CheckCircle, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/lib/theme';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { getLogo } from '@/config/project.config';
import { useDeviceFingerprint, getDeviceFingerprintSync } from '@/hooks/useDeviceFingerprint';
import { useLogin, useBootstrapStatus } from '@/hooks/api/useAuth';
import { useSlowOverlay } from '@/hooks/useSlowTransaction';
import { useNotification } from '@/hooks/useNotification';
import { getErrorMessage, isApiError } from '@/services/api/errors';

/* ── Validation schema ── */
const createLoginSchema = (t: (key: string) => string) =>
  z.object({
    email: z.string()
      .min(1, t('validation.required'))
      .email(t('validation.email')),
    password: z.string()
      .min(1, t('validation.required'))
      .min(8, t('validation.minLength').replace('{{min}}', '8')),
    rememberMe: z.boolean().optional(),
  });

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

export function LoginPage() {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const { isDark } = useTheme();
  const { fingerprint } = useDeviceFingerprint();
  const loginMutation = useLogin();
  useSlowOverlay(loginMutation.isPending);
  const { data: bootstrapStatus } = useBootstrapStatus();
  const { warning } = useNotification();
  const bootstrapCheckedRef = useRef(false);

  const [serverError, setServerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  // Only play boot animation on first visit of the day
  const [skipAnim, setSkipAnim] = useState(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      return localStorage.getItem('cx-login-anim-date') === today;
    } catch {
      return false;
    }
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  /* ── Bootstrap check ── */
  useEffect(() => {
    if (bootstrapStatus?.needsBootstrap && !bootstrapCheckedRef.current) {
      bootstrapCheckedRef.current = true;
      warning(t('auth.login.noBootstrap.message'), t('auth.login.noBootstrap.title'));
    }
  }, [bootstrapStatus, warning, t]);

  /* ── Success message from password reset ── */
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  /* ── Session expired ── */
  useEffect(() => {
    const msg = sessionStorage.getItem('session_expired');
    if (msg) {
      setSessionExpiredMessage(msg);
      sessionStorage.removeItem('session_expired');
    }
  }, []);

  /* ── Mark animation as played after boot sequence completes (~6s) ── */
  useEffect(() => {
    if (skipAnim) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem('cx-login-anim-date', new Date().toISOString().slice(0, 10));
      } catch { /* storage full or unavailable */ }
    }, 6000);
    return () => clearTimeout(timer);
  }, [skipAnim, animKey]);

  /* ── Form ── */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setServerError(null);
    setSuccessMessage(null);
    setSessionExpiredMessage(null);

    const deviceFp = fingerprint ?? getDeviceFingerprintSync();

    loginMutation.mutate(
      {
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        deviceFingerprint: deviceFp.hash,
        deviceName: deviceFp.info.browser,
        browser: deviceFp.info.browser,
        browserVersion: deviceFp.info.browserVersion,
        os: deviceFp.info.os,
        osVersion: deviceFp.info.osVersion,
      },
      {
        onError: (error) => {
          if (isApiError(error)) {
            setServerError(error.message || getErrorMessage(error.code));
          } else {
            setServerError(t('notifications.error'));
          }
        },
      },
    );
  };

  /* ── Canvas (particles + hex grid) ── */
  const initCanvas = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const cv = canvasRef.current;
    if (!cv) return;
    const parent = cv.parentElement;
    if (!parent) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    cv.width = parent.offsetWidth;
    cv.height = parent.offsetHeight;
    const W = cv.width;
    const H = cv.height;

    const ac  = isDark ? [230, 57, 70]  : [26, 95, 122];
    const ac2 = isDark ? [108, 99, 255] : [61, 150, 189];
    const sc  = isDark ? [154, 165, 184] : [74, 86, 112];

    const pts: { x: number; y: number; vx: number; vy: number; sz: number; o: number; col: number[] }[] = [];
    for (let i = 0; i < 50; i++) {
      pts.push({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
        sz: 0.6 + Math.random() * 2.2, o: 0.03 + Math.random() * 0.18,
        col: Math.random() > 0.8 ? ac2 : ac,
      });
    }

    let tick = 0;
    function draw() {
      tick++;
      ctx!.clearRect(0, 0, W, H);

      /* Hex grid */
      const br = 0.025 + Math.sin(tick * 0.008) * 0.008;
      const sz = 55;
      ctx!.strokeStyle = `rgba(${ac[0]},${ac[1]},${ac[2]},${br})`;
      ctx!.lineWidth = 0.5;
      const rows = Math.ceil(H / sz) + 2;
      const cols = Math.ceil(W / sz) + 2;
      for (let r = 0; r < rows; r++) {
        for (let co = 0; co < cols; co++) {
          const cx = co * sz * 1.5;
          const cy = r * sz * 1.73 + (co % 2) * sz * 0.866;
          ctx!.beginPath();
          for (let a = 0; a < 6; a++) {
            const ang = (Math.PI / 3) * a - Math.PI / 6;
            ctx![a === 0 ? 'moveTo' : 'lineTo'](cx + sz * 0.5 * Math.cos(ang), cy + sz * 0.5 * Math.sin(ang));
          }
          ctx!.closePath();
          ctx!.stroke();
        }
      }

      /* Particles */
      for (const pt of pts) {
        pt.x += pt.vx; pt.y += pt.vy;
        if (pt.x < 0 || pt.x > W) pt.vx *= -1;
        if (pt.y < 0 || pt.y > H) pt.vy *= -1;
        ctx!.beginPath();
        ctx!.arc(pt.x, pt.y, pt.sz, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(${pt.col[0]},${pt.col[1]},${pt.col[2]},${pt.o})`;
        ctx!.fill();
      }

      /* Connection lines */
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 130) {
            ctx!.beginPath();
            ctx!.moveTo(pts[i].x, pts[i].y);
            ctx!.lineTo(pts[j].x, pts[j].y);
            ctx!.strokeStyle = `rgba(${sc[0]},${sc[1]},${sc[2]},${0.035 * (1 - d / 130)})`;
            ctx!.lineWidth = 0.5;
            ctx!.stroke();
          }
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    }
    draw();
  }, [isDark]);

  useEffect(() => {
    initCanvas();
    const onResize = () => initCanvas();
    window.addEventListener('resize', onResize);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [initCanvas, animKey]);

  /* ════════════════════════════════════════════════════════════════════════ */

  return (
    <div className={`cx-login${skipAnim ? ' cx-no-anim' : ''}`}>
      {/* ── Controls (top-right) ── */}
      <div className="cx-ctrls">
        <LanguageToggle size="md" />
        <ThemeToggle showSystemOption size="md" />
      </div>

      <div key={animKey}>
        {/* ══ PHASE 0: PREBOOT ══ */}
        <div className="cx-preboot">
          <div style={{ textAlign: 'left' }}>
            <div className="cx-preboot-text">root@....x:~$ init --auth-gateway</div>
            <div className="cx-preboot-sub">&#9679; {t('auth.login.prebootSub')}</div>
          </div>
        </div>

        {/* ══ GLOBAL BOOT FX ══ */}
        <div className="cx-scanU" />
        <div className="cx-scanD" />
        <div className="cx-crt" />

        {/* ══ MAIN LAYOUT ══ */}
        <div className="cx-boot">
          <div className="cx-split">

            {/* ── LEFT — Brand ── */}
            <div className="cx-left">
              <canvas ref={canvasRef} />
              <div className="cx-brand">
                <div className="cx-lw">
                  <div className="cx-lr cx-lr1" />
                  <div className="cx-lr cx-lr2" />
                  <div className="cx-lr cx-lr3" />
                  <img
                    src={getLogo(isDark)}
                    alt="CoreX"
                    className="cx-logo-img"
                  />
                </div>
                <h1>COREX</h1>
                <div className="cx-sub">{t('auth.login.subtitle').toUpperCase()}</div>
                <div className="cx-tagline">&#9679; {t('auth.login.brandTagline')}</div>
                <div className="cx-modules">
                  {['VENTA', 'HR', 'COMPTA', 'PM', 'INVENT'].map((m) => (
                    <span key={m} className="cx-mod">{m}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── ORBIT SEPARATOR ── */}
            <div className="cx-vline">
              <div className="cx-dot" />
              <div className="cx-hcross" />
            </div>

            {/* ── RIGHT — Login form ── */}
            <div className="cx-right">
              <div className="cx-right-scanU" />
              <div className="cx-right-scanD" />
              <div className="cx-right-flash" />

              <form className="cx-fa" onSubmit={handleSubmit(onSubmit)}>
                {/* Heading */}
                <div className="cx-fd cx-d0">
                  <div className="cx-ftop">{t('auth.login.identification')}</div>
                  <div className="cx-fmain">{t('auth.login.formTitle')}</div>
                </div>

                {/* Dynamic alerts */}
                {sessionExpiredMessage && (
                  <div className="cx-alert cx-alert-warn">
                    <LogOut size={16} />
                    <span>{sessionExpiredMessage}</span>
                  </div>
                )}
                {successMessage && (
                  <div className="cx-alert cx-alert-ok">
                    <CheckCircle size={16} />
                    <span>{successMessage}</span>
                  </div>
                )}
                {serverError && (
                  <div className="cx-alert cx-alert-err">
                    <AlertTriangle size={16} />
                    <span>{serverError}</span>
                  </div>
                )}

                {/* Email */}
                <div className="cx-fd cx-d1">
                  <label className="cx-flb">{t('auth.login.email').toUpperCase()}</label>
                  <div className="cx-fiw">
                    <svg className="cx-fic" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="2" y="4" width="16" height="12" rx="2" />
                      <path d="M2 4l8 6 8-6" />
                    </svg>
                    <input
                      className={`cx-fin${errors.email ? ' cx-fin-err' : ''}`}
                      type="email"
                      placeholder={t('auth.login.emailPlaceholder')}
                      autoComplete="email"
                      {...register('email')}
                    />
                  </div>
                  {errors.email && <span className="cx-ferr">{errors.email.message}</span>}
                </div>

                {/* Password */}
                <div className="cx-fd cx-d2">
                  <label className="cx-flb">{t('auth.login.password').toUpperCase()}</label>
                  <div className="cx-fiw">
                    <svg className="cx-fic" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="5" y="9" width="10" height="8" rx="2" />
                      <path d="M7 9V6a3 3 0 016 0v3" />
                    </svg>
                    <input
                      className={`cx-fin${errors.password ? ' cx-fin-err' : ''}`}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.login.passwordPlaceholder')}
                      autoComplete="current-password"
                      {...register('password')}
                    />
                    <button
                      type="button"
                      className="cx-eyb"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M1 10s3-6 9-6 9 6 9 6-3 6-9 6-9-6-9-6z" />
                        <circle cx="10" cy="10" r="3" />
                      </svg>
                    </button>
                  </div>
                  {errors.password && <span className="cx-ferr">{errors.password.message}</span>}
                </div>

                {/* Remember me + Forgot password */}
                <div className="cx-fd cx-d3">
                  <div className="cx-frow">
                    <label className="cx-ckw">
                      <input type="checkbox" {...register('rememberMe')} />
                      <span className="cx-ckb" />
                      {t('auth.login.rememberMe')}
                    </label>
                    <Link to="/auth/password/request" className="cx-fgt">
                      {t('auth.login.forgotPassword')}
                    </Link>
                  </div>
                </div>

                {/* Submit */}
                <div className="cx-fd cx-d4">
                  <button
                    type="submit"
                    className="cx-sbtn"
                    disabled={loginMutation.isPending}
                  >
                    {loginMutation.isPending
                      ? t('auth.login.loggingIn').toUpperCase()
                      : t('auth.login.loginButton')}
                  </button>
                </div>

                {/* Footer */}
                <div className="cx-fd cx-d5">
                  <div className="cx-fdiv" />
                  <div className="cx-ffoot">
                    &copy; 2026 CoreX-Suite &middot; {t('authLayout.enterprisePlatform')}
                    <span style={{ margin: '0 0.375rem', color: 'var(--color-text-muted)' }}>&middot;</span>
                    <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</Link>
                    <span style={{ margin: '0 0.25rem' }}>&middot;</span>
                    <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</Link>
                  </div>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          STYLES
          ════════════════════════════════════════════════════════════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Outfit:wght@300;400;500;600;700;800&family=Share+Tech+Mono&family=Syne:wght@400;500;600;700;800&display=swap');

        /* ── Theme variables (scoped to login page) ── */
        [data-theme="dark"] .cx-login {
          --cx-bg:#06080C;--cx-bg2:#0C0E14;--cx-card:#10131A;--cx-card2:#181C26;
          --cx-bdr:#1E2330;--cx-bdr2:#2A3040;
          --cx-tx:#E8ECEF;--cx-tx2:#8A95AA;--cx-mt:#2E3648;--cx-dm:#4A5670;
          --cx-a1:#E63946;--cx-a2:#C41E3A;--cx-a3:#A31621;--cx-a4:#8B0000;--cx-a5:#FF4D5A;
          --cx-stl:#9AA5B8;--cx-grn:#0EA68D;--cx-pur:#6C63FF;
          --cx-inp-bg:rgba(22,26,36,0.95);--cx-inp-bdr:#1E2330;--cx-inp-foc:#E63946;
          --cx-glow:rgba(230,57,70,0.06);--cx-glow2:rgba(230,57,70,0.15);--cx-glow3:rgba(230,57,70,0.3);
          --cx-btn-bg:linear-gradient(135deg,#E63946 0%,#C41E3A 50%,#A31621 100%);--cx-btn-tx:#fff;
          --cx-link:#E63946;--cx-chk-bg:#181C26;--cx-chk-bdr:#2E3648;--cx-foot:#2E3648;
        }
        [data-theme="light"] .cx-login {
          --cx-bg:#EEF2F7;--cx-bg2:#E4E9F0;--cx-card:#FFFFFF;--cx-card2:#F3F6FA;
          --cx-bdr:#D0D8E4;--cx-bdr2:#B8C4D4;
          --cx-tx:#0E1218;--cx-tx2:#4A5670;--cx-mt:#8A95AA;--cx-dm:#5A6880;
          --cx-a1:#1A5F7A;--cx-a2:#16526B;--cx-a3:#12455C;--cx-a4:#6A8EA0;--cx-a5:#2C8AAE;
          --cx-stl:#4A5670;--cx-grn:#0EA68D;--cx-pur:#6C63FF;
          --cx-inp-bg:rgba(243,246,250,0.95);--cx-inp-bdr:#D0D8E4;--cx-inp-foc:#1A5F7A;
          --cx-glow:rgba(26,95,122,0.05);--cx-glow2:rgba(26,95,122,0.12);--cx-glow3:rgba(26,95,122,0.2);
          --cx-btn-bg:linear-gradient(135deg,#1A5F7A 0%,#16526B 50%,#12455C 100%);--cx-btn-tx:#fff;
          --cx-link:#1A5F7A;--cx-chk-bg:#F3F6FA;--cx-chk-bdr:#B8C4D4;--cx-foot:#8A95AA;
        }

        .cx-login {
          position:fixed;inset:0;overflow:hidden;
          font-family:'Outfit',sans-serif;background:var(--cx-bg);color:var(--cx-tx);
          transition:background 0.5s,color 0.4s;
          z-index:100;
        }

        /* ══ CONTROLS ══ */
        .cx-ctrls {
          position:fixed;top:16px;right:20px;z-index:500;
          display:flex;align-items:center;gap:8px;
          opacity:0;animation:cx-fadeIn 0.4s ease 5.5s forwards;
        }
        @keyframes cx-fadeIn{to{opacity:1}}

        /* ══ PHASE 0: PREBOOT (0 → 2.6s) ══ */
        .cx-preboot {
          position:fixed;inset:0;z-index:400;background:var(--cx-bg);
          display:flex;align-items:center;justify-content:center;
          animation:cx-prebootDie 0.5s ease 2.6s forwards;
        }
        @keyframes cx-prebootDie {
          0%{opacity:1;filter:brightness(1)}
          50%{opacity:1;filter:brightness(2.5)}
          100%{opacity:0;pointer-events:none;filter:brightness(1)}
        }
        .cx-preboot-text {
          font-family:'Share Tech Mono',monospace;font-size:0.7rem;letter-spacing:2px;color:var(--cx-a1);
          white-space:nowrap;overflow:hidden;border-right:2px solid var(--cx-a1);width:0;
          animation:cx-typeText 1.8s steps(34,end) 0.4s forwards,cx-blinkCur 0.6s step-end infinite;
        }
        @keyframes cx-typeText{to{width:34ch}}
        @keyframes cx-blinkCur{0%,100%{border-color:var(--cx-a1)}50%{border-color:transparent}}
        .cx-preboot-sub {
          font-family:'Share Tech Mono',monospace;font-size:0.42rem;letter-spacing:2px;
          color:var(--cx-grn);margin-top:12px;opacity:0;animation:cx-fadeIn 0.3s ease 2.0s forwards;
        }

        /* ══ PHASE 1: GLOBAL REVEAL (2.6s → 3.4s) ══ */
        .cx-boot {
          position:fixed;inset:0;
          clip-path:inset(50% 0 50% 0);
          animation:cx-bootSplit 1.0s cubic-bezier(0.33,1,0.68,1) 2.6s forwards;
          will-change:clip-path;
        }
        @keyframes cx-bootSplit{0%{clip-path:inset(50% 0 50% 0)}100%{clip-path:inset(0 0 0 0)}}
        .cx-scanU,.cx-scanD {
          position:fixed;left:0;right:0;height:2px;z-index:350;pointer-events:none;
          background:linear-gradient(90deg,transparent 2%,var(--cx-a1) 50%,transparent 98%);
        }
        .cx-scanU{top:50%;animation:cx-sUp 1.0s cubic-bezier(0.33,1,0.68,1) 2.6s both}
        .cx-scanD{bottom:50%;animation:cx-sDn 1.0s cubic-bezier(0.33,1,0.68,1) 2.6s both}
        @keyframes cx-sUp{0%{top:50%;opacity:0.9;height:2px}100%{top:0%;opacity:0;height:1px}}
        @keyframes cx-sDn{0%{bottom:50%;opacity:0.9;height:2px}100%{bottom:0%;opacity:0;height:1px}}
        .cx-crt {
          position:fixed;inset:0;z-index:340;pointer-events:none;
          background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(255,255,255,0.005) 2px,rgba(255,255,255,0.005) 4px);
          animation:cx-crtOff 2s ease 3.2s both;
        }
        @keyframes cx-crtOff{to{opacity:0}}

        /* ══ MAIN LAYOUT ══ */
        .cx-split{display:flex;min-height:100vh;align-items:stretch}

        /* ── LEFT — Brand ── */
        .cx-left{flex:1;position:relative;overflow:hidden;display:flex;align-items:center;justify-content:center}
        .cx-left canvas{position:absolute;inset:0;z-index:0}
        .cx-left::after {
          content:'';position:absolute;width:300px;height:300px;border-radius:50%;
          top:50%;left:50%;transform:translate(-50%,-50%);
          background:radial-gradient(circle,var(--cx-glow2),transparent 70%);
          animation:cx-ambPulse 4s ease-in-out infinite;z-index:1;pointer-events:none;
        }
        @keyframes cx-ambPulse{0%,100%{opacity:0.5;transform:translate(-50%,-50%) scale(1)}50%{opacity:1;transform:translate(-50%,-50%) scale(1.15)}}
        .cx-brand {
          position:relative;z-index:3;text-align:center;
          opacity:0;animation:cx-brandIn 0.7s cubic-bezier(0.22,1,0.36,1) 3.2s forwards;
        }
        @keyframes cx-brandIn{from{opacity:0;transform:scale(0.9) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}

        /* Logo wrapper + orbiting rings */
        .cx-lw{position:relative;width:130px;height:130px;margin:0 auto 18px}
        .cx-logo-img{width:100%;height:100%;position:relative;z-index:2;object-fit:contain}
        .cx-lr{position:absolute;border-radius:50%;border-style:solid;border-color:var(--cx-a1)}
        .cx-lr1{inset:-10px;border-width:1px;opacity:0.12;animation:cx-sp 10s linear infinite}
        .cx-lr2{inset:-22px;border-width:1px;opacity:0.06;animation:cx-sp 18s linear infinite reverse;border-style:dashed}
        .cx-lr3{inset:-36px;border-width:0.5px;opacity:0.03;animation:cx-sp 28s linear infinite;border-style:dotted}
        @keyframes cx-sp{to{transform:rotate(360deg)}}
        .cx-lw::before{content:'';position:absolute;inset:-6px;border-radius:50%;border:1px solid var(--cx-a1);opacity:0;animation:cx-haloPulse 2.5s ease-out infinite}
        .cx-lw::after{content:'';position:absolute;inset:-6px;border-radius:50%;border:1px solid var(--cx-a1);opacity:0;animation:cx-haloPulse 2.5s ease-out 1.25s infinite}
        @keyframes cx-haloPulse{0%{opacity:0.3;transform:scale(0.95)}100%{opacity:0;transform:scale(1.4)}}

        .cx-brand h1{font-family:'Bebas Neue',cursive;font-size:4.5rem;letter-spacing:20px;line-height:1}
        .cx-sub{font-family:'Share Tech Mono',monospace;font-size:0.48rem;letter-spacing:7px;color:var(--cx-mt);margin-top:6px}
        .cx-tagline{font-family:'Share Tech Mono',monospace;font-size:0.4rem;letter-spacing:3px;color:var(--cx-grn);margin-top:22px;opacity:0;animation:cx-fadeIn 0.3s ease 5s forwards}
        .cx-modules{display:flex;gap:5px;justify-content:center;margin-top:14px;opacity:0;animation:cx-fadeIn 0.4s ease 5.2s forwards}
        .cx-mod{font-family:'Share Tech Mono',monospace;font-size:0.35rem;letter-spacing:1.5px;padding:3px 8px;border-radius:3px;border:1px solid var(--cx-bdr);color:var(--cx-mt);transition:all 0.3s}
        .cx-mod:hover{border-color:var(--cx-a1);color:var(--cx-a1);box-shadow:0 0 12px var(--cx-glow)}

        /* ── VERTICAL LINE + ORBIT ── */
        .cx-vline{width:1px;position:relative;z-index:5;flex-shrink:0;overflow:visible}

        /* Gradient line — restored, always present at the separator */
        .cx-vline::after{
          content:'';position:absolute;left:0;right:0;top:50%;bottom:50%;
          background:linear-gradient(180deg,transparent,var(--cx-a1),transparent);
          animation:cx-vDraw 0.8s cubic-bezier(0.22,1,0.36,1) 3.4s forwards;
        }
        @keyframes cx-vDraw{0%{top:50%;bottom:50%;opacity:0}100%{top:5%;bottom:5%;opacity:0.2}}

        /* Arc dot — position-based, travels up/down the line (visible only during arc phase) */
        .cx-dot{
          position:absolute;left:-4px;width:9px;height:9px;border-radius:50%;
          background:var(--cx-a1);
          box-shadow:0 0 14px var(--cx-a1),0 0 28px color-mix(in srgb,var(--cx-a1) 50%,transparent);
          opacity:0;
          animation:cx-dotArc 16s ease-in-out 5s infinite;
        }
        @keyframes cx-dotArc{
          0%   {top:88%;opacity:0;  transform:scale(0.15)}
          4%   {         opacity:0.7;transform:scale(0.25)}
          10%  {top:50%; opacity:1;  transform:scale(1)}
          16%  {         opacity:0.7;transform:scale(0.25)}
          20%  {top:12%; opacity:0;  transform:scale(0.15)}
          20.1%{top:88%}
          85%  {top:88%; opacity:0;  transform:scale(0.15)}
          89%  {         opacity:0.7;transform:scale(0.25)}
          92.5%{top:50%; opacity:1;  transform:scale(1)}
          96%  {         opacity:0.7;transform:scale(0.25)}
          100% {top:12%; opacity:0;  transform:scale(0.15)}
        }

        /* Orbit wrap — slides left/right, no perspective (avoids distortion during travel) */
        .cx-orbit-wrap{
          position:absolute;top:50%;left:0;width:0;height:0;
          animation:cx-orbitSlide 16s cubic-bezier(0.37,0,0.63,1) 5s infinite;
        }
        @keyframes cx-orbitSlide{
          0%,20%  {transform:translateX(0)}
          35%,70% {transform:translateX(calc(-50vw + 250px))}
          85%,100%{transform:translateX(0)}
        }

        /* Scene — perspective lives here so slide doesn't distort */
        .cx-orbit-scene{position:absolute;width:0;height:0;perspective:500px;perspective-origin:0 0}

        /* Ring — 280px, rotates face-on over the logo, hidden during arc phase */
        .cx-orbit-ring{
          position:absolute;width:280px;height:280px;border-radius:50%;
          border:1px solid var(--cx-a1);
          box-shadow:0 0 16px color-mix(in srgb,var(--cx-a1) 35%,transparent),
                     0 0 50px color-mix(in srgb,var(--cx-a1) 12%,transparent);
          left:-140px;top:-140px;
          transform-style:preserve-3d;
          opacity:0;
          animation:cx-ringFlip 16s cubic-bezier(0.37,0,0.63,1) 5s infinite;
        }
        @keyframes cx-ringFlip{
          0%,18% {transform:rotateY(89deg);opacity:0}
          35%    {transform:rotateY(0deg); opacity:0.5}
          70%    {transform:rotateY(0deg); opacity:0.5}
          85%    {transform:rotateY(89deg);opacity:0}
          100%   {transform:rotateY(89deg);opacity:0}
        }

        /* Arm — constant angular velocity, always spinning */
        .cx-orbit-arm{
          position:absolute;width:140px;height:0;
          top:50%;left:50%;
          transform-origin:left center;
          animation:cx-armSpin 4s linear 5s infinite;
        }
        @keyframes cx-armSpin{from{transform:rotate(270deg)}to{transform:rotate(630deg)}}

        /* Orbit dot — visible only during circle phase */
        .cx-orbit-dot{
          position:absolute;right:-5px;top:-5px;width:10px;height:10px;border-radius:50%;
          background:var(--cx-a1);
          box-shadow:0 0 14px var(--cx-a1),0 0 28px color-mix(in srgb,var(--cx-a1) 50%,transparent);
          opacity:0;
          animation:cx-orbitDotFade 16s ease-in-out 5s infinite;
        }
        @keyframes cx-orbitDotFade{
          0%,28%{opacity:0}
          38%  {opacity:0.95}
          70%  {opacity:0.95}
          80%  {opacity:0}
          100% {opacity:0}
        }
        .cx-hcross {
          position:absolute;left:-30px;right:-30px;top:50%;height:1px;
          background:linear-gradient(90deg,transparent,var(--cx-a1),transparent);
          opacity:0;animation:cx-hcIn 0.5s ease 3.6s forwards;
        }
        @keyframes cx-hcIn{to{opacity:0.12}}

        /* ══ RIGHT PANEL ══ */
        .cx-right {
          width:480px;display:flex;align-items:center;justify-content:center;
          padding:40px;position:relative;z-index:2;
          clip-path:inset(50% 0 50% 0);
          animation:cx-rightSplit 0.8s cubic-bezier(0.33,1,0.68,1) 3.6s forwards;
          will-change:clip-path;
        }
        @keyframes cx-rightSplit{0%{clip-path:inset(50% 0 50% 0)}100%{clip-path:inset(0 0 0 0)}}
        .cx-right-scanU,.cx-right-scanD {
          position:absolute;left:0;right:0;height:2px;z-index:20;pointer-events:none;
          background:linear-gradient(90deg,transparent 5%,var(--cx-a1) 50%,transparent 95%);
        }
        .cx-right-scanU{animation:cx-rScanUp 0.8s cubic-bezier(0.33,1,0.68,1) 3.6s both}
        .cx-right-scanD{animation:cx-rScanDn 0.8s cubic-bezier(0.33,1,0.68,1) 3.6s both}
        @keyframes cx-rScanUp{0%{top:50%;opacity:0.7;height:2px}100%{top:0%;opacity:0;height:1px}}
        @keyframes cx-rScanDn{0%{bottom:50%;opacity:0.7;height:2px}100%{bottom:0%;opacity:0;height:1px}}
        .cx-right-flash {
          position:absolute;inset:0;z-index:15;pointer-events:none;
          background:var(--cx-a1);opacity:0;animation:cx-rFlash 0.4s ease 3.6s both;
        }
        @keyframes cx-rFlash{0%{opacity:0.04}100%{opacity:0}}

        /* ── Form area ── */
        .cx-fa{width:100%;max-width:390px;position:relative}
        .cx-fa::before {
          content:'';position:absolute;top:-8px;left:-40px;right:-40px;height:1px;
          background:linear-gradient(90deg,transparent,var(--cx-a1),transparent);
          opacity:0;transform:scaleX(0);animation:cx-hDraw 0.6s cubic-bezier(0.22,1,0.36,1) 4.2s forwards;
        }
        .cx-fa::after {
          content:'';position:absolute;bottom:-8px;left:-40px;right:-40px;height:1px;
          background:linear-gradient(90deg,transparent,var(--cx-a1),transparent);
          opacity:0;transform:scaleX(0);animation:cx-hDraw 0.5s cubic-bezier(0.22,1,0.36,1) 5.6s forwards;
        }
        @keyframes cx-hDraw{to{opacity:0.15;transform:scaleX(1)}}

        /* ── Staggered field reveals ── */
        .cx-fd{opacity:0;transform:translateY(12px);will-change:transform,opacity}
        .cx-d0{animation:cx-fdIn 0.5s cubic-bezier(0.22,1,0.36,1) 4.2s forwards}
        .cx-d1{animation:cx-fdIn 0.5s cubic-bezier(0.22,1,0.36,1) 4.45s forwards}
        .cx-d2{animation:cx-fdIn 0.5s cubic-bezier(0.22,1,0.36,1) 4.7s forwards}
        .cx-d3{animation:cx-fdIn 0.5s cubic-bezier(0.22,1,0.36,1) 4.95s forwards}
        .cx-d4{animation:cx-fdIn 0.5s cubic-bezier(0.22,1,0.36,1) 5.2s forwards}
        .cx-d5{animation:cx-fdIn 0.4s cubic-bezier(0.22,1,0.36,1) 5.5s forwards}
        @keyframes cx-fdIn{to{opacity:1;transform:translateY(0)}}

        /* ── Form elements ── */
        .cx-ftop{font-family:'Orbitron',sans-serif;font-size:0.7rem;font-weight:700;letter-spacing:6px;color:var(--cx-tx2);margin-bottom:2px}
        .cx-fmain {
          font-family:'Syne',sans-serif;font-size:1.7rem;font-weight:800;margin-bottom:24px;
          position:relative;display:inline-block;
        }
        .cx-fmain::after {
          content:'';position:absolute;bottom:-4px;left:0;width:40px;height:2px;
          background:var(--cx-a1);border-radius:1px;opacity:0;animation:cx-titleLine 0.4s ease 4.5s forwards;
        }
        @keyframes cx-titleLine{to{opacity:0.6}}

        .cx-flb{font-family:'Share Tech Mono',monospace;font-size:0.7rem;letter-spacing:3px;color:var(--cx-tx2);display:block;margin-bottom:6px;text-transform:uppercase;font-weight:700}
        .cx-fiw{position:relative;margin-bottom:18px}
        .cx-fic{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:var(--cx-mt);width:15px;height:15px;transition:color 0.3s}
        .cx-fin {
          width:100%;padding:14px 14px 14px 44px;
          background:var(--cx-inp-bg);border:1.5px solid var(--cx-inp-bdr);border-radius:0.375rem;
          color:var(--cx-tx);font-family:'Outfit',sans-serif;font-size:0.85rem;
          transition:border-color 0.3s,box-shadow 0.3s,background 0.3s;outline:none;
        }
        .cx-fin::placeholder{color:var(--cx-mt);font-size:0.78rem}
        .cx-fin:focus{border-color:var(--cx-inp-foc);box-shadow:0 0 0 3px var(--cx-glow),0 4px 16px var(--cx-glow);background:var(--cx-card2)}
        .cx-fin:focus~.cx-fic{color:var(--cx-a1)}
        .cx-fin-err{border-color:var(--cx-a1) !important}
        .cx-fiw::after{content:'';position:absolute;bottom:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--cx-a1),transparent);opacity:0;transition:opacity 0.3s}
        .cx-fiw:focus-within::after{opacity:0.3}
        .cx-ferr{display:block;font-family:'Share Tech Mono',monospace;font-size:0.42rem;letter-spacing:1px;color:var(--cx-a1);margin-top:-12px;margin-bottom:8px}

        .cx-eyb{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;color:var(--cx-mt);cursor:pointer;padding:4px;transition:color 0.2s}
        .cx-eyb:hover{color:var(--cx-tx2)}

        .cx-frow{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}
        .cx-ckw{display:flex;align-items:center;gap:8px;cursor:pointer;font-size:0.75rem;color:var(--cx-tx2)}
        .cx-ckb {
          width:16px;height:16px;border:1.5px solid var(--cx-chk-bdr);border-radius:4px;
          background:var(--cx-chk-bg);display:flex;align-items:center;justify-content:center;
          transition:all 0.25s;flex-shrink:0;
        }
        .cx-ckw:hover .cx-ckb{border-color:var(--cx-a1)}
        .cx-ckw input{display:none}
        .cx-ckw input:checked+.cx-ckb{background:var(--cx-a1);border-color:var(--cx-a1);box-shadow:0 0 8px var(--cx-glow)}
        .cx-ckw input:checked+.cx-ckb::after{content:'';width:4px;height:7px;border:2px solid #fff;border-top:none;border-left:none;transform:rotate(45deg);margin-top:-2px}

        .cx-fgt{font-family:'Share Tech Mono',monospace;font-size:0.7rem;letter-spacing:1px;color:var(--cx-link);text-decoration:none;transition:all 0.2s;font-weight:600}
        .cx-fgt:hover{opacity:0.8;letter-spacing:2px;text-decoration:underline}

        .cx-sbtn {
          width:100%;padding:15px;border:none;border-radius:0.375rem;
          background:var(--cx-btn-bg);color:var(--cx-btn-tx);
          font-family:'Orbitron',sans-serif;font-size:0.65rem;font-weight:700;letter-spacing:5px;
          cursor:pointer;position:relative;overflow:hidden;
          transition:all 0.35s cubic-bezier(0.22,1,0.36,1);
          box-shadow:0 4px 16px var(--cx-glow2);
        }
        .cx-sbtn:hover{transform:translateY(-2px);box-shadow:0 6px 24px var(--cx-glow3),0 0 40px var(--cx-glow2);letter-spacing:7px}
        .cx-sbtn:active{transform:translateY(0)}
        .cx-sbtn:disabled{opacity:0.7;cursor:not-allowed;transform:none !important;letter-spacing:5px !important}
        .cx-sbtn::before{content:'';position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent)}
        .cx-sbtn:hover::before{animation:cx-shine 0.6s ease}
        @keyframes cx-shine{to{left:100%}}
        .cx-sbtn::after{content:'';position:absolute;bottom:-1px;left:20%;right:20%;height:1px;background:linear-gradient(90deg,transparent,var(--cx-a5),transparent);opacity:0.5}

        .cx-fdiv{height:1px;background:linear-gradient(90deg,var(--cx-a1),transparent);margin:22px 0;opacity:0.1}
        .cx-ffoot{font-family:'Share Tech Mono',monospace;font-size:0.7rem;letter-spacing:1.5px;color:var(--color-text-secondary);text-align:center}

        /* ── Alerts ── */
        .cx-alert {
          display:flex;align-items:center;gap:10px;
          padding:10px 14px;border-radius:0.375rem;font-size:0.75rem;
          margin-bottom:14px;
          animation:cx-alertIn 0.3s ease forwards;
        }
        @keyframes cx-alertIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        .cx-alert-err{background:rgba(230,57,70,0.12);border:1px solid rgba(230,57,70,0.4);color:#FF4D5A}
        .cx-alert-ok{background:rgba(14,166,141,0.12);border:1px solid rgba(14,166,141,0.4);color:#0EA68D}
        .cx-alert-warn{background:rgba(251,191,36,0.12);border:1px solid rgba(251,191,36,0.4);color:#fbbf24}

        /* ── Responsive ── */
        @media (max-width:900px) {
          .cx-split{flex-direction:column}
          .cx-left{min-height:35vh}
          .cx-right{width:100%;padding:24px}
          .cx-vline{display:none}
          .cx-modules{display:none !important}
          .cx-brand h1{font-size:3rem;letter-spacing:12px}
        }

        /* ── No animation (return visit same day) ── */
        .cx-no-anim .cx-preboot { display:none !important }
        .cx-no-anim .cx-scanU,
        .cx-no-anim .cx-scanD,
        .cx-no-anim .cx-crt,
        .cx-no-anim .cx-right-scanU,
        .cx-no-anim .cx-right-scanD,
        .cx-no-anim .cx-right-flash { display:none !important }

        .cx-no-anim .cx-boot,
        .cx-no-anim .cx-right { clip-path:none !important; animation:none !important }

        .cx-no-anim .cx-brand,
        .cx-no-anim .cx-tagline,
        .cx-no-anim .cx-modules,
        .cx-no-anim .cx-ctrls,
        .cx-no-anim .cx-fd {
          opacity:1 !important;
          transform:none !important;
          animation:none !important;
        }

        .cx-no-anim .cx-fmain::after { opacity:0.6; animation:none !important }
        .cx-no-anim .cx-fa::before  { opacity:0.15; transform:scaleX(1); animation:none !important }
        .cx-no-anim .cx-fa::after   { opacity:0.15; transform:scaleX(1); animation:none !important }
        .cx-no-anim .cx-vline::after { top:5%; bottom:5%; opacity:0.2; animation:none !important }
        .cx-no-anim .cx-dot         { animation:cx-dotArcSimple 3s ease-in-out 0s infinite !important }
        .cx-no-anim .cx-orbit-wrap  { transform:translateX(0); animation:none !important }
        .cx-no-anim .cx-orbit-ring  { transform:rotateY(89deg); opacity:0; animation:none !important }
        .cx-no-anim .cx-orbit-arm   { transform:rotate(270deg); animation:none !important }
        .cx-no-anim .cx-orbit-dot   { opacity:0; animation:none !important }
        .cx-no-anim .cx-hcross      { opacity:0.12; animation:none !important }
        @keyframes cx-dotArcSimple {
          0%   { top:88%; opacity:0;   transform:scale(0.15) }
          15%  {           opacity:0.7; transform:scale(0.25) }
          50%  { top:50%; opacity:1;   transform:scale(1)    }
          85%  {           opacity:0.7; transform:scale(0.25) }
          100% { top:12%; opacity:0;   transform:scale(0.15) }
        }

        /* ── Reduced motion ── */
        @media (prefers-reduced-motion:reduce) {
          .cx-preboot,.cx-preboot-text,.cx-preboot-sub,
          .cx-boot,.cx-scanU,.cx-scanD,.cx-crt,
          .cx-brand,.cx-tagline,.cx-modules,.cx-ctrls,
          .cx-vline::after,.cx-dot,.cx-orbit-ring,.cx-orbit-arm,.cx-orbit-dot,.cx-hcross,
          .cx-right,.cx-right-scanU,.cx-right-scanD,.cx-right-flash,
          .cx-fd,.cx-fmain::after,.cx-fa::before,.cx-fa::after,
          .cx-lr,.cx-lw::before,.cx-lw::after,
          .cx-left::after,
          .cx-alert {
            animation:none !important;
            opacity:1 !important;
            transform:none !important;
            clip-path:none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default LoginPage;
